import { Routes } from '@angular/router';

/**
 * Catalog routes. Every entry is its own URL (`/design-system/<slug>`) so a
 * single component page is shareable with QA or a stakeholder.
 *
 * Every entry now has its own `pages/<slug>/` page, so there is no `:slug`
 * fallback and no legacy single-template catalog left to fall back to. An
 * unknown slug 404s, which is the honest answer.
 */
export const designSystemRoutes: Routes = [
  {
    path: 'design-system',
    title: 'Design System',
    loadComponent: () => import('./shell/catalog-shell.component'),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'overview' },
      {
        path: 'overview',
        title: 'Design System — Overview',
        loadComponent: () => import('./pages/overview/overview.page'),
      },
      {
        path: 'input',
        title: 'Design System — Input',
        loadComponent: () => import('./pages/input/input.page'),
      },
      {
        path: 'number-input',
        title: 'Design System — Number Input',
        loadComponent: () => import('./pages/number-input/number-input.page'),
      },
      {
        path: 'password-input',
        title: 'Design System — Password Input',
        loadComponent: () => import('./pages/password-input/password-input.page'),
      },
      {
        path: 'label',
        title: 'Design System — Label',
        loadComponent: () => import('./pages/label/label.page'),
      },
      {
        path: 'multiline-input',
        title: 'Design System — Multiline Input',
        loadComponent: () => import('./pages/multiline-input/multiline-input.page'),
      },
      {
        path: 'checkbox',
        title: 'Design System — Checkbox',
        loadComponent: () => import('./pages/checkbox/checkbox.page'),
      },
      {
        path: 'switch',
        title: 'Design System — Switch',
        loadComponent: () => import('./pages/switch/switch.page'),
      },
      {
        path: 'radio-group',
        title: 'Design System — Radio Group',
        loadComponent: () => import('./pages/radio-group/radio-group.page'),
      },
      {
        path: 'color-input',
        title: 'Design System — Color Input',
        loadComponent: () => import('./pages/color-input/color-input.page'),
      },
      {
        path: 'date-input',
        title: 'Design System — Date Input',
        loadComponent: () => import('./pages/date-input/date-input.page'),
      },
      {
        path: 'date-time-input',
        title: 'Design System — Date Time Input',
        loadComponent: () => import('./pages/date-time-input/date-time-input.page'),
      },
      {
        path: 'time-input',
        title: 'Design System — Time Input',
        loadComponent: () => import('./pages/time-input/time-input.page'),
      },
      {
        path: 'date-picker',
        title: 'Design System — Date Picker',
        loadComponent: () => import('./pages/date-picker/date-picker.page'),
      },
      {
        path: 'date-range-picker',
        title: 'Design System — Date Range Picker',
        loadComponent: () => import('./pages/date-range-picker/date-range-picker.page'),
      },
      {
        path: 'date-range-input',
        title: 'Design System — Date Range Input',
        loadComponent: () => import('./pages/date-range-input/date-range-input.page'),
      },
      {
        path: 'date-range-field',
        title: 'Design System — Date Range Field',
        loadComponent: () => import('./pages/date-range-field/date-range-field.page'),
      },
      {
        path: 'date-range-dropdown',
        title: 'Design System — Date Range Dropdown',
        loadComponent: () =>
          import('./pages/date-range-dropdown/date-range-dropdown.page'),
      },
      {
        path: 'page-filter',
        title: 'Design System — Page Filter',
        loadComponent: () => import('./pages/page-filter/page-filter.page'),
      },
      {
        path: 'button',
        title: 'Design System — Button',
        loadComponent: () => import('./pages/button/button.page'),
      },
      {
        path: 'button-group',
        title: 'Design System — Button Group',
        loadComponent: () => import('./pages/button-group/button-group.page'),
      },
      {
        path: 'split-button',
        title: 'Design System — Split Button',
        loadComponent: () => import('./pages/split-button/split-button.page'),
      },
      {
        path: 'context-menu',
        title: 'Design System — Context Menu',
        loadComponent: () => import('./pages/context-menu/context-menu.page'),
      },
      {
        path: 'card',
        title: 'Design System — Card',
        loadComponent: () => import('./pages/card/card.page'),
      },
      {
        path: 'cell-grid',
        title: 'Design System — Cell Grid',
        loadComponent: () => import('./pages/cell-grid/cell-grid.page'),
      },
      {
        path: 'item',
        title: 'Design System — Item',
        loadComponent: () => import('./pages/item/item.page'),
      },
      {
        path: 'badge',
        title: 'Design System — Badge',
        loadComponent: () => import('./pages/badge/badge.page'),
      },
      {
        path: 'module-item',
        title: 'Design System — Module Item',
        loadComponent: () => import('./pages/module-item/module-item.page'),
      },
      {
        path: 'dot',
        title: 'Design System — Dot',
        loadComponent: () => import('./pages/dot/dot.page'),
      },
      {
        path: 'avatar',
        title: 'Design System — Avatar',
        loadComponent: () => import('./pages/avatar/avatar.page'),
      },
      {
        path: 'avatar-group',
        title: 'Design System — Avatar Group',
        loadComponent: () => import('./pages/avatar-group/avatar-group.page'),
      },
      {
        path: 'kbd',
        title: 'Design System — Kbd',
        loadComponent: () => import('./pages/kbd/kbd.page'),
      },
      {
        path: 'number-badge',
        title: 'Design System — Number Badge',
        loadComponent: () => import('./pages/number-badge/number-badge.page'),
      },
      {
        path: 'icon',
        title: 'Design System — Icon',
        loadComponent: () => import('./pages/icon/icon.page'),
      },
      {
        path: 'chip',
        title: 'Design System — Chip',
        loadComponent: () => import('./pages/chip/chip.page'),
      },
      {
        path: 'chat-bubble',
        title: 'Design System — Chat Bubble',
        loadComponent: () => import('./pages/chat-bubble/chat-bubble.page'),
      },
      {
        path: 'kennzeichen',
        title: 'Design System — Kennzeichen',
        loadComponent: () => import('./pages/kennzeichen/kennzeichen.page'),
      },
      {
        path: 'separator',
        title: 'Design System — Separator',
        loadComponent: () => import('./pages/separator/separator.page'),
      },
      {
        path: 'loading-spinner',
        title: 'Design System — Loading Spinner',
        loadComponent: () => import('./pages/loading-spinner/loading-spinner.page'),
      },
      {
        path: 'skeleton',
        title: 'Design System — Skeleton',
        loadComponent: () => import('./pages/skeleton/skeleton.page'),
      },
      {
        path: 'empty-state',
        title: 'Design System — Empty State',
        loadComponent: () => import('./pages/empty-state/empty-state.page'),
      },
      {
        path: 'progress-bars',
        title: 'Design System — Progress Bars',
        loadComponent: () => import('./pages/progress-bars/progress-bars.page'),
      },
      {
        path: 'process-path',
        title: 'Design System — Process Path',
        loadComponent: () => import('./pages/process-path/process-path.page'),
      },
      {
        path: 'progress-steps',
        title: 'Design System — Progress Steps',
        loadComponent: () => import('./pages/progress-steps/progress-steps.page'),
      },
      {
        path: 'slider',
        title: 'Design System — Slider',
        loadComponent: () => import('./pages/slider/slider.page'),
      },
      {
        path: 'kpi',
        title: 'Design System — KPI',
        loadComponent: () => import('./pages/kpi/kpi.page'),
      },
      {
        path: 'notification',
        title: 'Design System — Notification',
        loadComponent: () => import('./pages/notification/notification.page'),
      },
      {
        path: 'tabs',
        title: 'Design System — Tabs',
        loadComponent: () => import('./pages/tabs/tabs.page'),
      },
      {
        path: 'accordion',
        title: 'Design System — Accordion',
        loadComponent: () => import('./pages/accordion/accordion.page'),
      },
      {
        path: 'timeline',
        title: 'Design System — Timeline',
        loadComponent: () => import('./pages/timeline/timeline.page'),
      },
      {
        path: 'toggle',
        title: 'Design System — Toggle',
        loadComponent: () => import('./pages/toggle/toggle.page'),
      },
      {
        path: 'overlay',
        title: 'Design System — Overlay',
        loadComponent: () => import('./pages/overlay/overlay.page'),
      },
      {
        path: 'panel-header',
        title: 'Design System — Panel Header',
        loadComponent: () => import('./pages/panel-header/panel-header.page'),
      },
      {
        path: 'field-row',
        title: 'Design System — Field Row',
        loadComponent: () => import('./pages/field-row/field-row.page'),
      },
      {
        path: 'form-group',
        title: 'Design System — Form Group',
        loadComponent: () => import('./pages/form-group/form-group.page'),
      },
      {
        path: 'prev-curr-next-navigation',
        title: 'Design System — Prev / Curr / Next Navigation',
        loadComponent: () =>
          import('./pages/prev-curr-next-navigation/prev-curr-next-navigation.page'),
      },
      {
        path: 'quantity-input',
        title: 'Design System — Quantity Input',
        loadComponent: () => import('./pages/quantity-input/quantity-input.page'),
      },
      {
        path: 'select',
        title: 'Design System — Select',
        loadComponent: () => import('./pages/select/select.page'),
      },
      {
        path: 'colors',
        title: 'Design System — Farben',
        loadComponent: () => import('./pages/colors/colors.page'),
      },
      {
        path: 'action-matrix',
        title: 'Design System — Aktionsmatrix',
        loadComponent: () => import('./pages/action-matrix/action-matrix.page'),
      },
      {
        path: 'spacing',
        title: 'Design System — Abstände',
        loadComponent: () => import('./pages/spacing/spacing.page'),
      },
      {
        path: 'typography',
        title: 'Design System — Typografie',
        loadComponent: () => import('./pages/typography/typography.page'),
      },
      {
        path: 'elevation',
        title: 'Design System — Tiefe',
        loadComponent: () => import('./pages/elevation/elevation.page'),
      },
      {
        path: 'responsive-wizard',
        title: 'Design System — Responsive Wizard',
        loadComponent: () => import('./pages/responsive-wizard/responsive-wizard.page'),
      },
    ],
  },
];
